import { useEffect, useState, useCallback } from "react";

type KeyInputHandler = (event: KeyboardEvent) => boolean | undefined;

/**
 * Hook for waiting for keyboard input to assign a hotkey.
 * Returns [isWaiting, startWaiting, stopWaiting] tuple.
 */
export const useWaitForKeyInput = (onKeyPress: KeyInputHandler) => {
  const [isWaiting, setIsWaiting] = useState(false);

  const startWaiting = useCallback(() => {
    setIsWaiting(true);
  }, []);

  const stopWaiting = useCallback(() => {
    setIsWaiting(false);
  }, []);

  useEffect(() => {
    if (!isWaiting) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      event.preventDefault();
      event.stopPropagation();

      const result = onKeyPress(event);
      if (result !== false) {
        setIsWaiting(false);
      }
    };

    const handleClick = () => {
      setIsWaiting(false);
    };

    document.addEventListener("keydown", handleKeyDown, true);
    document.addEventListener("click", handleClick, true);

    return () => {
      document.removeEventListener("keydown", handleKeyDown, true);
      document.removeEventListener("click", handleClick, true);
    };
  }, [isWaiting, onKeyPress]);

  return [isWaiting, startWaiting, stopWaiting] as const;
};
